import { useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { usePinch } from '@use-gesture/react';
import { cn } from '@shared/lib/cn';
import { openLink } from '@shared/lib/openLink';
import { MESSAGE_BODY_CLASS } from './messageHtml';

const MAX_ZOOM = 3;
const DOUBLE_TAP_ZOOM = 2;
const DOUBLE_TAP_MS = 300;
/** Further than this between down and up is a pan or a pinch, not a tap. */
const TAP_SLOP_PX = 10;

/**
 * A rendered message body the member can pinch to 3x and pan.
 *
 * The page is zoom-locked app-wide (index.html), so zoom lives here. CSS `zoom`
 * lays the body out at its 1x width and scales it, so text stays sharp and this
 * scroller gains real overflow to pan sideways; the page scrolls it vertically.
 * At 1x the body fits the screen and a wide table scrolls sideways inside it.
 */
export function MessageBody({ html, className }: { html: string; className?: string }): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const [baseWidth, setBaseWidth] = useState(0);
  const pendingScroll = useRef<{ left: number; down: number } | null>(null);
  const tapStart = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef(0);

  // 1. Zoom around a screen point, so that point stays under the fingers.
  //    This scroller absorbs the sideways shift, the page the downward one.
  const zoomTo = (next: number, clientX: number, clientY: number): void => {
    const scroller = scrollerRef.current!;
    if (zoomRef.current === 1) setBaseWidth(scroller.clientWidth);

    const ratio = next / zoomRef.current;
    const rect = scroller.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    pendingScroll.current = { left: (scroller.scrollLeft + x) * ratio - x, down: y * (ratio - 1) };

    zoomRef.current = next;
    setZoom(next);
  };

  // 2. Pinch between 1x and 3x. Touch events rather than pointer events: WebKit keeps
  //    delivering them while it scrolls.
  usePinch(({ origin: [x, y], offset: [scale] }) => zoomTo(scale, x, y), {
    target: scrollerRef,
    from: () => [zoomRef.current, 0],
    scaleBounds: { min: 1, max: MAX_ZOOM },
    pointer: { touch: true },
  });

  // 3. Double-tap toggles 1x / 2x. A single tap passes through, so links stay tappable.
  //    Only the primary finger counts, and only if it stayed put.
  const onPointerDown = (event: PointerEvent): void => {
    tapStart.current = event.isPrimary ? { x: event.clientX, y: event.clientY } : null;
  };

  const onPointerUp = (event: PointerEvent): void => {
    const start = tapStart.current;
    if (!event.isPrimary || !start) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_SLOP_PX) return;

    if (event.timeStamp - lastTap.current > DOUBLE_TAP_MS) {
      lastTap.current = event.timeStamp;
      return;
    }
    lastTap.current = 0;
    zoomTo(zoomRef.current > 1 ? 1 : DOUBLE_TAP_ZOOM, event.clientX, event.clientY);
  };

  // 4. Links open through openLink: the in-app browser on native, a new tab on the web.
  //    The sanitiser's target/rel stay for any link this handler never sees.
  const onClick = (event: MouseEvent): void => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!link) return;
    event.preventDefault();
    void openLink(link.href);
  };

  // 5. Apply the new zoom's scroll before paint, so the body doesn't jump.
  useLayoutEffect(() => {
    const pending = pendingScroll.current;
    if (!pending) return;
    scrollerRef.current!.scrollLeft = pending.left;
    scrollParent(scrollerRef.current!)?.scrollBy(0, pending.down);
    pendingScroll.current = null;
  }, [zoom]);

  return (
    <div
      ref={scrollerRef}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onClick={onClick}
      className={cn('touch-pan-x touch-pan-y overflow-x-auto', className)}
    >
      <div
        className={cn('max-w-none', MESSAGE_BODY_CLASS)}
        style={zoom > 1 ? { zoom, width: baseWidth } : undefined}
        // Sanitised by toRenderableMessageHtml; admin-authored HTML.
        dangerouslySetInnerHTML={{ __html: html || '<p class="text-ink-muted italic">(no content)</p>' }}
      />
    </div>
  );
}

/** The nearest ancestor that scrolls vertically: the screen or sheet the body sits in. */
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}
